import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Camera, Search, Star, Pin, X } from "lucide-react";

import Layout from "../components/Layout";
import { api } from "../lib/api";
import useIsMobile from "../lib/useIsMobile";
import {
  getFavoriteCaseIds,
  togglePinCase,
  FAVORITES_LIMIT,
} from "../lib/chairsideStore";
import { emit, EVENTS } from "../lib/analytics";
import { toast } from "sonner";

// On smartphones we cap the "Recent Cases" list to keep the chairside
// surface focused on the last-few-minutes-of-work view.
const MOBILE_RECENT_LIMIT = 10;

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "ready", label: "Ready" },
  { id: "pending", label: "Pending" },
  { id: "today", label: "Today" },
];

function _statusOf(a) {
  if (a.is_legacy) return "legacy";
  if (a.review_ready) return "ready";
  return "pending";
}

function _sameDay(iso, ref = new Date()) {
  try {
    const d = new Date(iso);
    return d.getFullYear() === ref.getFullYear() &&
           d.getMonth() === ref.getMonth() &&
           d.getDate() === ref.getDate();
  } catch (_) { return false; }
}

function _matchesQuery(a, q) {
  if (!q) return true;
  const needle = q.toLowerCase().trim();
  // Case ID (last 6 chars)
  if (a.id && a.id.toLowerCase().includes(needle)) return true;
  if (a.id && a.id.slice(-6).toLowerCase().includes(needle)) return true;
  // Patient initials — during Preview these live in routing_note prefix,
  // and are opt-in synthetic. Full text search on routing_note (non-PHI in
  // Preview) is acceptable.
  if (a.routing_note && a.routing_note.toLowerCase().includes(needle)) return true;
  // Date — accept "2026-08", "aug", "8/4", etc.
  if (a.created_at && a.created_at.toLowerCase().includes(needle)) return true;
  try {
    const d = new Date(a.created_at);
    const human = d.toLocaleDateString().toLowerCase();
    const monthShort = d.toLocaleDateString(undefined, { month: "short" }).toLowerCase();
    if (human.includes(needle) || monthShort.includes(needle)) return true;
  } catch (_) { /* ignore */ }
  // Status keywords
  const s = _statusOf(a);
  if (s.includes(needle)) return true;
  return false;
}

export default function QuickAssessmentList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [favIds, setFavIds] = useState(() => getFavoriteCaseIds());

  useEffect(() => {
    api.get("/quick-assessment")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
    emit(EVENTS.FEATURE_OPEN, { feature: "recent_cases", path: "/quick-assessment" });
  }, []);

  const filtered = useMemo(() => {
    let out = items;
    if (statusFilter !== "all") {
      if (statusFilter === "today") out = out.filter((a) => _sameDay(a.created_at));
      else out = out.filter((a) => _statusOf(a) === statusFilter);
    }
    if (query) out = out.filter((a) => _matchesQuery(a, query));
    return out;
  }, [items, statusFilter, query]);

  const favoriteItems = useMemo(() => {
    if (!favIds.length) return [];
    const byId = new Map(items.map((a) => [a.id, a]));
    return favIds.map((id) => byId.get(id)).filter(Boolean);
  }, [favIds, items]);

  // Recents = filtered minus pinned; capped on mobile.
  const recentItems = useMemo(() => {
    const pinSet = new Set(favIds);
    const rest = filtered.filter((a) => !pinSet.has(a.id));
    return isMobile ? rest.slice(0, MOBILE_RECENT_LIMIT) : rest;
  }, [filtered, favIds, isMobile]);

  const handleTogglePin = (caseId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const res = togglePinCase(caseId);
    setFavIds(res.ids);
    if (res.limit) {
      toast.warning(`You can pin up to ${FAVORITES_LIMIT} favourites. Unpin one first.`);
      return;
    }
    if (res.pinned) {
      emit(EVENTS.FAVORITE_PINNED, { feature: "recent_cases", input_mode: "tap" });
      toast.success("Pinned to Favorites.");
    } else {
      emit(EVENTS.FAVORITE_UNPINNED, { feature: "recent_cases", input_mode: "tap" });
      toast.info("Removed from Favorites.");
    }
  };

  const renderRow = (a, { pinned = false } = {}) => (
    <div
      key={a.id}
      data-testid={`qa-row-${a.id}`}
      className="relative"
    >
      <Link
        to={`/quick-assessment/${a.id}`}
        className="flex items-center justify-between p-4 pr-14 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-md hover:border-[var(--pa-gold)]/60 transition group"
      >
        <div>
          <div className="text-xs uppercase tracking-wider">
            {a.is_legacy ? (
              <span className="text-[var(--pa-text-dim)]">Historical record</span>
            ) : a.review_ready ? (
              <span className="text-emerald-300">Review ready</span>
            ) : (
              <span className="text-amber-300">Not yet review ready</span>
            )}
          </div>
          <div className="mt-1 text-sm text-[var(--pa-text)] line-clamp-1 max-w-xl">
            <span className="text-[var(--pa-text-dim)] mr-1">#{a.id.slice(-6).toUpperCase()}</span>
            {a.is_legacy ? "Historical assessment (legacy)" : (a.routing_note || "Case intake")}
          </div>
          <div className="text-[10px] text-[var(--pa-text-dim)] mt-1">
            {a.image_count} photo{a.image_count === 1 ? "" : "s"} · {new Date(a.created_at).toLocaleString()}
          </div>
        </div>
        <ArrowRight size={16} className="text-[var(--pa-text-dim)] group-hover:text-[var(--pa-gold)]" />
      </Link>
      <button
        onClick={(e) => handleTogglePin(a.id, e)}
        data-testid={`qa-pin-${a.id}`}
        aria-label={pinned ? "Unpin case" : "Pin case"}
        className={`absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center transition ${
          pinned
            ? "bg-[var(--pa-gold)]/25 text-[var(--pa-gold)] hover:bg-[var(--pa-gold)]/40"
            : "bg-[var(--pa-bg-2)] text-[var(--pa-text-dim)] hover:text-[var(--pa-gold)]"
        }`}
      >
        {pinned ? <Star size={14} fill="currentColor" /> : <Star size={14} />}
      </button>
    </div>
  );

  return (
    <Layout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="kicker">
            {isMobile ? "Chairside · Recent" : "Tier 1 · Case Intake"}
          </div>
          <h1
            data-testid="qa-list-title"
            className="font-serif text-3xl text-[var(--pa-text)] mt-1"
          >
            {isMobile ? "Recent Cases" : "Quick Assessments"}
          </h1>
          {isMobile && (
            <div
              data-testid="qa-list-mobile-subtitle"
              className="mt-2 text-xs text-[var(--pa-text-dim)]"
            >
              Favorites and your last {MOBILE_RECENT_LIMIT} case intakes.
            </div>
          )}
        </div>
        {!isMobile && (
          <Link
            to="/quick-assessment/new"
            data-testid="qa-list-new-btn"
            className="flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-4 py-2.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
          >
            <Camera size={14} /> New quick assessment
          </Link>
        )}
      </div>

      {/* Search + status filter — one-hand friendly */}
      <div className="mt-6 space-y-3" data-testid="qa-list-search-region">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pa-text-dim)]" />
          <input
            data-testid="qa-list-search-input"
            type="search"
            inputMode="search"
            enterKeyHint="search"
            placeholder="Search by ID, initials, date, or status"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full h-11 pl-9 pr-9 rounded-md bg-[var(--pa-surface)] border border-[var(--pa-border)] text-[var(--pa-text)] placeholder:text-[var(--pa-text-dim)] focus:outline-none focus:border-[var(--pa-gold)]"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="qa-list-search-clear"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[var(--pa-text-dim)] hover:text-[var(--pa-text)]"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto -mx-1 px-1" data-testid="qa-list-filter-row">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              data-testid={`qa-list-filter-${f.id}`}
              className={`shrink-0 text-xs uppercase tracking-wider px-3 py-2 rounded-full border transition min-h-[36px] ${
                statusFilter === f.id
                  ? "bg-[var(--pa-gold)] text-[var(--pa-bg)] border-[var(--pa-gold)]"
                  : "bg-transparent text-[var(--pa-text-muted)] border-[var(--pa-border)] hover:border-[var(--pa-gold)]/60"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6" data-testid="qa-list">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--pa-text-muted)]">
            <Loader2 size={14} className="animate-spin text-[var(--pa-gold)]" /> Loading…
          </div>
        ) : (items.length === 0) ? (
          <div className="p-10 text-center text-sm text-[var(--pa-text-muted)] border border-dashed border-[var(--pa-border)] rounded-md">
            {isMobile
              ? "No recent cases yet. Start one with Case Intake."
              : "No quick assessments yet. Snap a photo to prepare a 30-second case intake for specialist review."}
          </div>
        ) : (
          <>
            {favoriteItems.length > 0 && (
              <div className="mb-6" data-testid="qa-favorites-section">
                <div className="kicker mb-2 flex items-center gap-2">
                  <Pin size={11} /> Favorites <span className="text-[var(--pa-text-dim)] normal-case">({favoriteItems.length}/{FAVORITES_LIMIT})</span>
                </div>
                <div className="space-y-3">
                  {favoriteItems.map((a) => renderRow(a, { pinned: true }))}
                </div>
              </div>
            )}

            {recentItems.length > 0 ? (
              <>
                {favoriteItems.length > 0 && (
                  <div className="kicker mb-2">Recent</div>
                )}
                <div className="space-y-3">
                  {recentItems.map((a) => renderRow(a))}
                </div>
              </>
            ) : (query || statusFilter !== "all") ? (
              <div
                data-testid="qa-list-empty-filter"
                className="p-6 text-center text-sm text-[var(--pa-text-muted)] border border-dashed border-[var(--pa-border)] rounded-md"
              >
                No cases match this filter.
              </div>
            ) : null}
          </>
        )}

        {isMobile && !loading && (
          <Link
            to="/quick-assessment/new"
            data-testid="qa-list-mobile-new-cta"
            className="mt-6 flex items-center justify-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-4 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
          >
            <Camera size={16} /> Start a new case intake
          </Link>
        )}
      </div>
    </Layout>
  );
}
